import { Controller } from "@hotwired/stimulus"
import { loadStripe } from "@stripe/stripe-js/pure"
import { reactor } from "../../lib/reactor"

export default class extends Controller {
  static values = {
    pk: String,
    secret: String,
    vatCountries: Array,
    setupIntentId: String,
    vatNumber: String,
    firstName: String,
    lastName: String,
    email: String,
    phone: String,
    city: String,
    country: String,
    line1: String,
    line2: String,
    zip: String,
    state: String,
    setupIntentParam: { type: String, default: "input[setup_intent_id]" },
  }

  connect() {
    // Pair with disconnect. Morph would detach Stripe iframes while confirmCardSetup
    // still holds this.cardNumber. See initializers/turbo_morph_coordinator.js.
    document.dispatchEvent(new CustomEvent("fsp:increment"))
    reactor(this)
    this.prepareForm()
    this.listen()
  }

  disconnect() {
    this.ignore()
    document.dispatchEvent(new CustomEvent("fsp:decrement"))
  }

  async prepareForm() {
    if (this.setupIntentIdValue) {
      this.addSetupIntentIdNode()
    } else {
      this.stripe = await loadStripe(this.pkValue)
      this.elements = this.stripe.elements()
      this.cardNumber = this.elements.create("cardNumber", this.elementOptions)
      this.cardExpiry = this.elements.create("cardExpiry", this.elementOptions)
      this.cardCvc = this.elements.create("cardCvc", this.elementOptions)
      this.cardNumber.mount("#stripe-card-number")
      this.cardExpiry.mount("#stripe-card-expiry")
      this.cardCvc.mount("#stripe-card-cvc")
    }
  }

  updateElements = () => {
    this.cardNumber?.update(this.elementOptions)
    this.cardExpiry?.update(this.elementOptions)
    this.cardCvc?.update(this.elementOptions)
  }

  get elementOptions() {
    return  {
      style: {
        base: {
          iconColor: '#c4f0ff',
          color: this.isDarkMode ? "#E6E7EB" : "#000",
          fontWeight: '500',
          fontFamily: 'Inter, sans-serif',
          fontSize: '16px',
          fontSmoothing: 'antialiased',
          ':focus': {
            color: '#3C76BC'
          }
        },
        invalid: {
          iconColor: '#DC2627',
          color: '#DC2627',
        }
      }
    }
  }

  get isDarkMode() {
    const root = document.documentElement
    if (root.classList.contains("dark")) return true
    if (root.classList.contains("light")) return false
    return !!(window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches)
  }

  countryValueChanged = () => {
    if (!this.vatRequired())
      this.vatNumberValue = ""
  }

  vatNumberValueChanged = () => {
    this.vatNumberValue = this.vatNumberValue.toUpperCase().replace(this.vatNumberRegExp, "")
  }

  get vatNumberRegExp() {
    return new RegExp(`^(${this.vatCountriesValue.join("|")})`)
  }

  vatRequired() {
    return this.vatCountriesValue.includes(this.countryValue)
  }

  listen() {
    this.colorSchemeQuery = window.matchMedia("(prefers-color-scheme: dark)")
    this.colorSchemeQuery.addEventListener("change", this.updateElements)
    document.addEventListener("theme:changed", this.updateElements)
    this.element.addEventListener("submit", this.onSubmit)
  }

  ignore() {
    this.colorSchemeQuery?.removeEventListener("change", this.updateElements)
    document.removeEventListener("theme:changed", this.updateElements)
    this.element.removeEventListener("submit", this.onSubmit)
    this.cardNumber?.unmount()
    this.cardExpiry?.unmount()
    this.cardCvc?.unmount()
  }

  onSubmit = async (event) => {
    this.removeBaseError()

    if (!this.setupIntentIdValue) {
      event.preventDefault()

      if (await this.acquirePaymentIntent()) {
        this.addSetupIntentIdNode()
        this.element.dispatchEvent(new CustomEvent("submit", { bubbles: true }))
      }
    }
  }

  async acquirePaymentIntent() {
    const {setupIntent, error} = await this.stripe.confirmCardSetup(
      this.secretValue,
      {
        payment_method: {
          card: this.cardNumber,
          billing_details: this.buildBillingDetails()
        }
      }
    )

    if (error) {
      this.setBaseError(error.message)
      this.element.dispatchEvent(new CustomEvent("spinner:to-button"))
      return false
    }

    if (setupIntent.status === "succeeded") {
      this.setupIntentIdValue = setupIntent.id
      return true
    }

    return false
  }

  buildBillingDetails() {
    let billingDetails = { address: { } }

    if (this.firstNameValue && this.lastNameValue)
      billingDetails.name = `${this.firstNameValue} ${this.lastNameValue}`

    if (this.emailValue)
      billingDetails.email = this.emailValue

    if (this.phoneValue)
      billingDetails.phone = this.phoneValue

    if (this.cityValue)
      billingDetails.address.city = this.cityValue

    if (this.countryValue)
      billingDetails.address.country = this.countryValue

    if (this.line1Value)
      billingDetails.address.line1 = this.line1Value

    if (this.line2Value)
      billingDetails.address.line2 = this.line2Value

    if (this.zipValue)
      billingDetails.address.postal_code = this.zipValue

    if (this.stateValue)
      billingDetails.address.state = this.stateValue

    return billingDetails
  }

  get baseErrorTarget() {
    return this.element.querySelector("[data-id=base-error]")
  }

  setBaseError(message) {
    this.baseErrorTarget.innerText = message
  }

  removeBaseError() {
    this.baseErrorTarget.innerText = ""
  }

  addSetupIntentIdNode() {
    const existing = this.element.querySelector("input[name$='[setup_intent_id]']")
    if (existing) {
      existing.value = this.setupIntentIdValue
      return
    }

    const setupIntentIdNode = document.createElement("input")
    setupIntentIdNode.type = "hidden"
    setupIntentIdNode.name = this.setupIntentParamValue
    setupIntentIdNode.value = this.setupIntentIdValue
    this.element.prepend(setupIntentIdNode)
  }
}
